import { ref } from 'vue'
import { api } from '@/api/api.js'
import { useImageCompression } from '@/composables/useImageCompression'

// Растровые изображения, которые имеет смысл сжимать на клиенте.
// svg (вектор) и gif (анимация) пропускаем — их сжатие через canvas ломает файл.
const COMPRESSIBLE_IMAGE_TYPES = /^image\/(jpe?g|png|webp|bmp|avif)$/i

export function useFileUpload() {
  const file = ref<File | null>(null)
  const fileName = ref('')
  const uploadedFileId = ref<number | null>(null)
  const isDragging = ref(false)

  const { compressImage } = useImageCompression()

  const prepareFile = async (selected: File): Promise<File> => {
    if (!COMPRESSIBLE_IMAGE_TYPES.test(selected.type)) return selected
    try {
      return await compressImage(selected)
    } catch (error) {
      // Сжатие не критично: при ошибке (напр. HEIC, недоступный worker) грузим оригинал.
      console.error('Не удалось сжать изображение, загружаю оригинал:', error)
      return selected
    }
  }

  const processFile = async (selected: File): Promise<void> => {
    file.value = selected
    fileName.value = selected.name

    const toUpload = await prepareFile(selected)

    const formData = new FormData()
    formData.append('file', toUpload)
    try {
      const data = await api.uploadFile(formData)
      uploadedFileId.value = data.id
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error)
    }
  }

  const handleFileSelect = (event: Event): void => {
    const input = event.target as HTMLInputElement
    const selected = input.files?.[0]
    if (selected) void processFile(selected)
  }

  const handleDrop = (event: DragEvent): void => {
    const selected = event.dataTransfer?.files?.[0]
    if (selected) void processFile(selected)
    isDragging.value = false
  }

  const clearFile = (): void => {
    setTimeout(() => {
      file.value = null
      fileName.value = ''
    }, 50)
  }

  const triggerFileUpload = (): void => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '*/*'
    input.addEventListener('change', handleFileSelect)
    input.click()
  }

  return {
    file,
    fileName,
    uploadedFileId,
    isDragging,
    processFile,
    handleFileSelect,
    handleDrop,
    clearFile,
    triggerFileUpload,
  }
}
